import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react'
import { useEditor, EditorContent, ReactRenderer } from '@tiptap/react'
import { Extension } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Mention from '@tiptap/extension-mention'
import tippy from 'tippy.js'
import EmojiPicker from 'emoji-picker-react'
import { LinkDialog } from './LinkDialog'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import { ReactComponent as FileUploadIcon } from 'assets/svgs/file-upload-icon.svg'
import { ReactComponent as MentionIcon } from 'assets/svgs/mention-icon.svg'
import { ReactComponent as HyperlinkIcon } from 'assets/svgs/hyperlink-icon.svg'
import { ReactComponent as BoldIcon } from 'assets/svgs/bold-icon.svg'
import { ReactComponent as ItalicIcon } from 'assets/svgs/italic-icon.svg'
import { ReactComponent as StrikethroughIcon } from 'assets/svgs/strikethrought-icon.svg'
import { ReactComponent as ListBulletIcon } from 'assets/svgs/list-bullet-icon.svg'
import { ReactComponent as TrashIcon } from 'assets/svgs/trash.svg'
import { AppSendMessageIcon } from 'components/Common/AppSvgIcons/AppSendMessageIcon'

// Import Tiptap CSS
// import '@tiptap/core/style.css'

// Mention list component
interface MentionListProps {
  items: Array<{ id: string; label: string; email: string; firstName: string; lastName: string }>
  command: (item: {
    id: string
    label: string
    email: string
    firstName: string
    lastName: string
  }) => void
  selectedIndex: number
}

const MentionList = React.forwardRef<HTMLDivElement, MentionListProps>((props, ref) => {
  const { items, command, selectedIndex } = props

  const selectItem = useCallback(
    (index: number) => {
      const item = items[index]
      if (item) {
        command(item)
      }
    },
    [items, command]
  )

  useEffect(() => {
    const navigationKeys = ['ArrowUp', 'ArrowDown', 'Enter']
    const onKeyDown = (e: KeyboardEvent) => {
      if (navigationKeys.includes(e.key)) {
        e.preventDefault()
        if (e.key === 'ArrowUp') {
          selectItem((selectedIndex + items.length - 1) % items.length)
        } else if (e.key === 'ArrowDown') {
          selectItem((selectedIndex + 1) % items.length)
        } else if (e.key === 'Enter') {
          selectItem(selectedIndex)
        }
        return true
      }
      return false
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [selectedIndex, selectItem, items.length])

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg shadow-lg absolute bottom-full mb-2 left-0 right-0 z-50 min-w-[320px] max-h-[280px] overflow-y-auto"
      ref={ref}
    >
      {items.length ? (
        items.map((item, index) => {
          const isCoachLogic = item.id === 'coachLogic'
          const initial = item.label
            ?.split(' ')
            .map((name: string) => name[0])
            .join('')

          return (
            <button
              key={item.id}
              className={`w-full px-4 py-3 cursor-pointer transition-colors duration-150 text-left ${
                index === selectedIndex ? 'bg-gray-50' : 'hover:bg-gray-50'
              }`}
              onClick={() => selectItem(index)}
            >
              <div className="flex items-center gap-3">
                {isCoachLogic ? (
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-purple-100 text-purple-600 text-lg">
                    🤖
                  </div>
                ) : (
                  <AppAvatar size="sm" type={'mixed'} shape="Rounded" text={initial} />
                )}
                <div className="flex flex-col min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 truncate">{item.label}</div>
                  {isCoachLogic && (
                    <span className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">
                      AI Assistant
                    </span>
                  )}
                </div>
              </div>
            </button>
          )
        })
      ) : (
        <div className="px-4 py-3 text-sm text-gray-500">No result</div>
      )}
    </div>
  )
})

MentionList.displayName = 'MentionList'

export interface MentionItem {
  id: string
  label: string
  email?: string
  firstName?: string
  lastName?: string
}

export interface UploadedAttachment {
  type: 'image' | 'file'
  url: string
  fileName: string
  mimeType: string
  fileSize: number
}

export interface SubmitPayload {
  text: string
  html: string
  mentions: MentionItem[]
  attachments: UploadedAttachment[]
  hasMentions: boolean
}

type UploadProgressHandler = (progress: number) => void

interface RichTextInputProps {
  placeholder?: string
  onSubmit?: (payload: SubmitPayload) => void | Promise<void>
  onTextChange?: (text: string) => void
  initialContent?: string
  value?: string
  onChange?: (html: string) => void
  mode?: 'message' | 'form'
  disabled?: boolean
  rightActions?: React.ReactNode
  onAttachClick?: () => void
  mentionProvider?: (query: string) => Promise<MentionItem[]> | MentionItem[]
  initialMentions?: MentionItem[]
  uploadAdapter?: (file: File, onProgress?: UploadProgressHandler) => Promise<UploadedAttachment>
  hasVoiceMessage?: boolean
}
export const RichTextInput: React.FC<RichTextInputProps> = ({
  placeholder = 'Type a message...',
  onSubmit,
  onTextChange,
  initialContent = '',
  value,
  onChange,
  mode = 'message',
  disabled = false,
  rightActions,
  onAttachClick,
  mentionProvider,
  initialMentions = [],
  uploadAdapter,
  hasVoiceMessage = false,
}) => {
  const isControlled = value !== undefined
  const contentToUse = isControlled ? value : initialContent
  // Helper to check if content is empty (handles HTML strings)
  const isContentEmpty = (content: string | undefined): boolean => {
    if (!content) return true
    // Strip HTML tags and check if there's any text
    const textOnly = content.replace(/<[^>]*>/g, '').trim()
    return textOnly.length === 0
  }
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [showLinkDialog, setShowLinkDialog] = useState(false)
  // Initialize isEditorEmpty based on controlled value or initialContent
  const [isEditorEmpty, setIsEditorEmpty] = useState(() =>
    isContentEmpty(isControlled ? value : initialContent)
  )
  const [linkDialogData, setLinkDialogData] = useState<{
    text: string
    url: string
    isEditing: boolean
  }>({ text: '', url: '', isEditing: false })
  const emojiButtonRef = useRef<HTMLButtonElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const pendingUploadsRef = useRef<
    Array<{
      id: string
      file: File
      status: 'queued' | 'uploading' | 'uploaded' | 'failed'
      url?: string
      isImage: boolean
      progress: number
      startedAt?: number
      etaSeconds?: number
      previewUrl?: string
    }>
  >([])
  const [pendingUploads, setPendingUploads] = useState<
    Array<{
      id: string
      file: File
      status: 'queued' | 'uploading' | 'uploaded' | 'failed'
      url?: string
      isImage: boolean
      progress: number
      startedAt?: number
      etaSeconds?: number
      previewUrl?: string
    }>
  >([])
  useEffect(() => {
    pendingUploadsRef.current = pendingUploads
  }, [pendingUploads])
  useEffect(() => {
    return () => {
      pendingUploadsRef.current.forEach((upload) =>
        upload.previewUrl ? URL.revokeObjectURL(upload.previewUrl) : null
      )
    }
  }, [])

  const clearPendingUploads = useCallback(() => {
    setPendingUploads((prev) => {
      prev.forEach((upload) => upload.previewUrl && URL.revokeObjectURL(upload.previewUrl))
      return []
    })
  }, [])

  const removePendingUpload = useCallback((id: string) => {
    setPendingUploads((prev) => {
      const target = prev.find((upload) => upload.id === id)
      if (target?.previewUrl) {
        URL.revokeObjectURL(target.previewUrl)
      }
      return prev.filter((upload) => upload.id !== id)
    })
  }, [])

  // Create mention suggestions from team members
  // Build mention suggestions if/when mention feature is re-enabled
  // This will be populated when integrated with Stream Chat or when mentionProvider is used
  const mentionSuggestions: MentionItem[] = useMemo(() => [], [])

  // Create custom keymap extension with mode closure
  const customKeymapExtension = useMemo(
    () =>
      Extension.create({
        name: 'customListKeymap',
        priority: 1000,
        addKeyboardShortcuts() {
          return {
            'Shift-Enter': () => {
              if (this.editor.isActive('bulletList') || this.editor.isActive('orderedList')) {
                return this.editor.chain().focus().splitListItem('listItem').run()
              }
              return false
            },
            // In message mode, swallow Enter to prevent new bullet items (submission handled elsewhere)
            // In form mode, allow Enter to create newlines
            Enter: () => {
              if (mode === 'message') {
                return true // Swallow Enter in message mode
              }
              return false // Allow default Enter behavior in form mode
            },
            // Handle backspace in lists to convert back to plain text
            Backspace: () => {
              const { state } = this.editor
              const { $from, empty } = state.selection

              // Only handle if we're in a list and at the start of the list item content
              if (
                (this.editor.isActive('bulletList') || this.editor.isActive('orderedList')) &&
                empty &&
                $from.parentOffset === 0
              ) {
                // Lift the list item to convert it back to a paragraph
                return this.editor.chain().focus().liftListItem('listItem').run()
              }

              return false // Allow default backspace behavior
            },
          }
        },
      }),
    [mode]
  )

  const editor = useEditor({
    extensions: [
      // Custom keymap: Shift+Enter in lists creates a new list item (no hard break)
      customKeymapExtension,
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        hardBreak: {},
      }),
      Link.configure({
        openOnClick: true,
        autolink: true,
        defaultProtocol: 'https',
        HTMLAttributes: {
          class: 'text-app-primary-60 underline hover:text-app-primary-70 cursor-pointer',
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }).extend({
        inclusive: false,
      }),
      Mention.configure({
        HTMLAttributes: {
          class: 'mention bg-app-primary-10 rounded px-1 py-0.5 text-app-primary-70 font-medium',
        },
        renderText({ node }) {
          return `@${node.attrs.label || node.attrs.id}`
        },
        renderHTML({ node }) {
          return [
            'mention',
            {
              id: node.attrs.id,
            },
            `@${node.attrs.label}`,
          ]
        },
        suggestion: {
          items: ({ query }) => {
            const q = (query || '').toLowerCase()
            if (mentionProvider) {
              return mentionProvider(q) as any
            }
            return mentionSuggestions.filter((item) => item.label.toLowerCase().includes(q))
          },
          render: () => {
            let component: ReactRenderer | null = null
            let popup: any
            return {
              onStart: (props) => {
                component = new ReactRenderer(MentionList as any, {
                  props: {
                    ...props,
                    command: (item: any) => {
                      props.command({
                        id: item.id,
                        label: item.label,
                      } as any)
                    },
                  },
                  editor: props.editor,
                })
                popup = tippy('body', {
                  getReferenceClientRect: props.clientRect as any,
                  appendTo: () => document.body,
                  content: (component as any).element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: 'manual',
                  placement: 'top-start',
                  offset: [0, 8],
                  theme: 'light',
                })
              },
              onUpdate(props) {
                component?.updateProps({
                  ...props,
                  command: (item: any) => {
                    props.command({
                      id: item.id,
                      label: item.label,
                    } as any)
                  },
                })
                popup?.[0]?.setProps({
                  getReferenceClientRect: props.clientRect,
                })
              },
              onKeyDown(props) {
                if (props.event.key === 'Escape') {
                  popup?.[0]?.hide()
                  return true
                }
                return (component as any)?.ref?.onKeyDown?.(props.event) ?? false
              },
              onExit() {
                if (popup && popup[0] && !popup[0].state.isDestroyed) {
                  popup[0].destroy()
                }
                if (component) {
                  component.destroy()
                }
              },
            }
          },
        },
      }),
    ],
    content: contentToUse || '',
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const text = editor.getText()
      const html = editor.getHTML()
      onTextChange?.(text)
      // In controlled mode, call onChange with HTML
      if (isControlled && onChange) {
        onChange(html)
      }
      // Check if editor is truly empty - also consider active formatting like bullet lists
      const hasActiveFormatting = editor.isActive('bulletList') || editor.isActive('orderedList')
      setIsEditorEmpty(editor.isEmpty && !hasActiveFormatting)
    },
    onSelectionUpdate: ({ editor }) => {
      // Auto-deactivate link formatting when cursor moves away from links
      if (!editor.isActive('link')) {
        // This helps prevent new text from being included in links
      }
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[70px] px-3 py-2',
        'data-placeholder': placeholder,
      } as any,
    },
  })

  // Sync isEditorEmpty with editor's actual state when editor is created or content changes
  useEffect(() => {
    if (!editor) return
    // Check editor's isEmpty state to ensure placeholder visibility is correct
    // Also consider active formatting like bullet lists
    const hasActiveFormatting = editor.isActive('bulletList') || editor.isActive('orderedList')
    setIsEditorEmpty(editor.isEmpty && !hasActiveFormatting)
  }, [editor])

  // Handle emoji insertion
  const handleEmojiClick = useCallback(
    (emojiData: any) => {
      if (editor) {
        editor.chain().focus().insertContent(emojiData.emoji).run()
        setShowEmojiPicker(false)
      }
    },
    [editor]
  )

  // Handle link dialog
  const handleLinkClick = useCallback(() => {
    if (!editor) return

    // Check if there's already a link at the current position
    if (editor.isActive('link')) {
      // If there's a link, select the entire link first
      editor.chain().focus().extendMarkRange('link').run()
    }

    // Get selected text (after potential extension)
    const selectedText = editor.state.doc.textBetween(
      editor.state.selection.from,
      editor.state.selection.to
    )

    const { from, to } = editor.state.selection
    const linkMark = editor.state.doc.rangeHasMark(from, to, editor.schema.marks.link)

    if (linkMark) {
      // If there's a link, get its attributes for editing
      const linkAttrs = editor.getAttributes('link')
      setLinkDialogData({
        text: selectedText || linkAttrs.href || '',
        url: linkAttrs.href || '',
        isEditing: true,
      })
    } else {
      setLinkDialogData({
        text: selectedText,
        url: '',
        isEditing: false,
      })
    }

    setShowLinkDialog(true)
  }, [editor])

  const handleLinkSave = useCallback(
    (text: string, url: string) => {
      if (!editor || !url) return

      const linkText = text || url

      // Helper to insert link and prevent bleeding (typing into the link)
      // We insert the link as HTML to ensure proper rendering
      // Then we explicitly clear the 'link' mark from storedMarks so the next typed char is clean
      const chain = editor.chain().focus()

      if (editor.state.selection.from !== editor.state.selection.to || editor.isActive('link')) {
        // Editing: select full link first
        chain.extendMarkRange('link')
      }

      chain
        .insertContent(`<a href="${url}">${linkText}</a>`)
        .command(({ tr, state }) => {
          const linkType = state.schema.marks.link
          // Get current marks at cursor (which is now at end of inserted link)
          // We default to the marks present at the cursor position
          const currentMarks = tr.storedMarks || state.storedMarks || state.selection.$from.marks()

          // Remove link mark from the set of marks for the next input
          const newMarks = currentMarks.filter((m) => m.type !== linkType)
          tr.setStoredMarks(newMarks)

          return true
        })
        .run()
    },
    [editor]
  )

  const handleLinkRemove = useCallback(() => {
    if (!editor) return
    editor.chain().focus().unsetLink().run()
  }, [editor])

  // File handling: managed locally in rich text input
  const triggerFilePicker = useCallback(() => {
    // Only trigger the file input directly - onAttachClick is handled separately in parent component
    fileInputRef.current?.click()
  }, [])

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const items: Array<{
      id: string
      file: File
      status: 'queued'
      url?: string
      isImage: boolean
      progress: number
      startedAt?: number
      etaSeconds?: number
      previewUrl?: string
    }> = []

    Array.from(files).forEach((file) => {
      if (file.size > 5 * 1024 * 1024) {
        alert(`File ${file.name} exceeds the 5MB limit.`)
        return
      }

      const isImage = file.type?.startsWith('image/')
      items.push({
        id: `${Date.now()}-${file.name}-${Math.random()}`,
        file,
        status: 'queued',
        isImage,
        progress: 0,
        startedAt: undefined,
        etaSeconds: undefined,
        previewUrl: isImage ? URL.createObjectURL(file) : undefined,
      })
    })
    setPendingUploads((prev) => [...prev, ...items])
    e.target.value = ''
  }, [])

  // Handle form submission
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (!editor) return

      const text = editor.getText()
      const html = editor.getHTML()

      // Custom HTML serializer to ensure mentions are properly formatted
      let customHtml = ''
      let hasMentions = false

      // Helper function to serialize text with marks
      const serializeTextWithMarks = (textNode: any): string => {
        let textContent = textNode.text
        if (textNode.marks) {
          textNode.marks.forEach((mark: any) => {
            if (mark.type?.name === 'bold') {
              textContent = `<strong>${textContent}</strong>`
            } else if (mark.type?.name === 'italic') {
              textContent = `<em>${textContent}</em>`
            } else if (mark.type?.name === 'strike') {
              textContent = `<s>${textContent}</s>`
            } else if (mark.type?.name === 'link') {
              textContent = `<a href="${mark.attrs.href}">${textContent}</a>`
            }
          })
        }
        return textContent
      }

      // Helper function to serialize paragraph content
      const serializeParagraphContent = (paragraph: any): string => {
        let paragraphContent = ''
        paragraph.content.forEach((child: any) => {
          if (child.type?.name === 'text') {
            paragraphContent += serializeTextWithMarks(child)
          } else if (child.type?.name === 'mention') {
            paragraphContent += `<mention id="${child.attrs.id}">@${child.attrs.label}</mention>`
            hasMentions = true
          }
        })
        return paragraphContent
      }

      // Process only top-level nodes to avoid duplication
      editor.state.doc.content.forEach((node: any) => {
        if (node.type?.name === 'paragraph') {
          const paragraphContent = serializeParagraphContent(node)
          customHtml += `<p>${paragraphContent}</p>`
        } else if (node.type?.name === 'bulletList') {
          customHtml += '<ul>'
          node.content.forEach((listItem: any) => {
            if (listItem.type?.name === 'listItem') {
              customHtml += '<li>'
              listItem.content.forEach((paragraph: any) => {
                if (paragraph.type?.name === 'paragraph') {
                  customHtml += serializeParagraphContent(paragraph)
                }
              })
              customHtml += '</li>'
            }
          })
          customHtml += '</ul>'
        } else if (node.type?.name === 'orderedList') {
          customHtml += '<ol>'
          node.content.forEach((listItem: any) => {
            if (listItem.type?.name === 'listItem') {
              customHtml += '<li>'
              listItem.content.forEach((paragraph: any) => {
                if (paragraph.type?.name === 'paragraph') {
                  customHtml += serializeParagraphContent(paragraph)
                }
              })
              customHtml += '</li>'
            }
          })
          customHtml += '</ol>'
        }
      })

      // Use custom HTML if we have mentions, otherwise use regular HTML
      const finalHtml = hasMentions ? customHtml.trim() : html

      // Check for uploads (local only)
      const hasUploads = pendingUploads.length > 0

      // If nothing to send and no uploads queued, bail
      if (!text.trim() && !hasUploads) return

      try {
        // Upload pending files via provided adapter and build attachments
        const attachments: UploadedAttachment[] = []
        if (pendingUploads.length > 0 && uploadAdapter) {
          for (const item of pendingUploads) {
            setPendingUploads((prev) =>
              prev.map((u) =>
                u.id === item.id
                  ? {
                      ...u,
                      status: 'uploading',
                      progress: 0,
                      etaSeconds: undefined,
                      startedAt: Date.now(),
                    }
                  : u
              )
            )
            try {
              const uploaded = await uploadAdapter(item.file, (progress) => {
                setPendingUploads((prev) =>
                  prev.map((u) =>
                    u.id === item.id
                      ? {
                          ...u,
                          progress,
                          etaSeconds:
                            progress > 0 && u.startedAt
                              ? Math.max(
                                  0,
                                  ((100 - progress) / progress) *
                                    ((Date.now() - u.startedAt) / 1000)
                                )
                              : undefined,
                        }
                      : u
                  )
                )
              })
              attachments.push(uploaded)
              setPendingUploads((prev) =>
                prev.map((u) =>
                  u.id === item.id
                    ? { ...u, status: 'uploaded', url: uploaded.url, progress: 100, etaSeconds: 0 }
                    : u
                )
              )
            } catch (err) {
              console.error('Upload failed for file:', item.file.name, err)
              setPendingUploads((prev) =>
                prev.map((u) => (u.id === item.id ? { ...u, status: 'failed' } : u))
              )
            }
          }
        }

        // Collect mentioned users from document in the same format as MentionTextArea
        const mentionedUserIds: string[] = []
        const mentions: any[] = []
        editor.state.doc.descendants((node: any) => {
          if (node.type?.name === 'mention' && node.attrs?.id) {
            const mentionData = mentionSuggestions.find((s) => s.id === node.attrs.id)
            mentionedUserIds.push(String(node.attrs.id))
            mentions.push({
              id: node.attrs.id,
              display: node.attrs.label,
              email: mentionData?.email || '',
              firstName: mentionData?.firstName || '',
              lastName: mentionData?.lastName || '',
            })
          }
        })

        // Emit payload to parent
        await onSubmit?.({
          text: hasMentions ? finalHtml : text,
          html: finalHtml,
          attachments,
          mentions,
          hasMentions,
        })
        editor.commands.clearContent()
        clearPendingUploads()
      } catch (err) {
        editor.commands.clearContent()
      }
    },
    [editor, pendingUploads, uploadAdapter, onSubmit, clearPendingUploads, mentionSuggestions]
  )

  // Sync editor content when value prop changes (controlled mode)
  useEffect(() => {
    if (!editor || !isControlled) return
    const currentHtml = editor.getHTML()
    if (currentHtml !== value) {
      editor.commands.setContent(value || '', false)
      setIsEditorEmpty(editor.isEmpty)
    }
  }, [value, editor, isControlled])

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!editor) return

    const handleKeyDown = (event: KeyboardEvent) => {
      // Handle Enter key - only submit in message mode
      if (mode === 'message' && event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        event.stopPropagation()
        handleSubmit(event as any)
      }

      // Handle Ctrl/Cmd + B for bold
      if ((event.ctrlKey || event.metaKey) && event.key === 'b') {
        event.preventDefault()
        editor.chain().focus().toggleBold().run()
      }

      // Handle Ctrl/Cmd + I for italic
      if ((event.ctrlKey || event.metaKey) && event.key === 'i') {
        event.preventDefault()
        editor.chain().focus().toggleItalic().run()
      }

      // Handle Ctrl/Cmd + K for link
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault()
        handleLinkClick()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [editor, handleSubmit, handleLinkClick, mode])

  if (!editor) {
    return null
  }

  // Helper function to format file size
  const formatFileSizeDisplay = (bytes: number): string => {
    if (bytes < 1000) return `${bytes} B`
    if (bytes < 1_000_000) return `${Math.round(bytes / 1000)} KB`
    if (bytes < 1_000_000_000) return `${Math.round(bytes / 1_000_000)} MB`
    return `${Math.round(bytes / 1_000_000_000)} GB`
  }

  return (
    <div className="rich-text-input-container">
      <form onSubmit={handleSubmit} className="flex flex-col">
        {/* Input container matching design */}
        <div className="border border-app-primary-30 rounded-xl bg-white focus-within:border-app-primary-60 transition-colors shadow">
          {/* Pending uploads inside container */}
          {pendingUploads.length > 0 && (
            <div className="px-3 pt-3 pb-2 space-y-2 border-b border-app-grey-25">
              {pendingUploads.map((u) => (
                <div
                  key={u.id}
                  className="flex items-center gap-3 p-2 bg-app-grey-5 rounded-lg border border-app-grey-25"
                >
                  <div className="flex-shrink-0">
                    {u.isImage && u.previewUrl ? (
                      <img
                        src={u.previewUrl}
                        alt={u.file.name}
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-app-primary-60 flex items-center justify-center">
                        <svg
                          className="w-6 h-6 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* File info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-app-grey-80 truncate">{u.file.name}</p>
                    <p className="text-xs text-app-grey-60 mt-0.5">
                      {formatFileSizeDisplay(u.file.size)}
                    </p>

                    {/* Loading indicator */}
                    {u.status === 'uploading' && (
                      <div className="mt-1.5">
                        <div className="w-full bg-gray-200 rounded-full h-1">
                          <div
                            className="bg-app-primary-60 h-1 rounded-full transition-all duration-300"
                            style={{ width: `${u.progress}%` }}
                          />
                        </div>
                      </div>
                    )}
                    {u.status === 'queued' && (
                      <p className="text-xs text-app-grey-60 mt-1">Queued for upload</p>
                    )}
                    {u.status === 'failed' && (
                      <p className="text-xs text-red-500 mt-1">Upload failed</p>
                    )}
                  </div>

                  {/* Remove button with trash icon */}
                  <button
                    type="button"
                    onClick={() => removePendingUpload(u.id)}
                    className="flex-shrink-0 p-1 text-app-grey-60 hover:text-red-500 transition-colors"
                    aria-label="Remove file"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Editor with placeholder overlay */}
          <div className="relative">
            {editor ? <EditorContent editor={editor} /> : null}
            {isEditorEmpty && !disabled && (
              <div className="pointer-events-none absolute left-3 right-3 top-2 text-app-grey-60 text-sm">
                {placeholder}
              </div>
            )}
          </div>

          {/* Toolbar inside container bottom */}
          <div className="flex items-center justify-between px-2 py-2">
            <div className="flex items-center gap-1">
              {/* Attach - only show if onAttachClick is provided */}
              {onAttachClick && (
                <>
                  <button
                    type="button"
                    onClick={triggerFilePicker}
                    className="p-2 rounded hover:bg-app-grey-20 transition-colors"
                    title="Attach file"
                  >
                    <FileUploadIcon />
                  </button>
                  {/* Hidden file input for attachments */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </>
              )}

              {/* Emoji */}
              <div className="relative">
                <button
                  ref={emojiButtonRef}
                  type="button"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className="p-2 rounded hover:bg-app-grey-20 transition-colors"
                  title="Insert Emoji"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12.6517 12.6517C11.1872 14.1161 8.81282 14.1161 7.34835 12.6517M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10ZM8.125 8.125C8.125 8.47018 7.98509 8.75 7.8125 8.75C7.63991 8.75 7.5 8.47018 7.5 8.125C7.5 7.77982 7.63991 7.5 7.8125 7.5C7.98509 7.5 8.125 7.77982 8.125 8.125ZM7.8125 8.125H7.81875V8.1375H7.8125V8.125ZM12.5 8.125C12.5 8.47018 12.3601 8.75 12.1875 8.75C12.0149 8.75 11.875 8.47018 11.875 8.125C11.875 7.77982 12.0149 7.5 12.1875 7.5C12.3601 7.5 12.5 7.77982 12.5 8.125ZM12.1875 8.125H12.1938V8.1375H12.1875V8.125Z"
                      stroke="#3F3F46"
                      stroke-width="1.25"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
                {showEmojiPicker && (
                  <div className="absolute bottom-full mb-2 left-0 z-50">
                    <EmojiPicker onEmojiClick={(emojiData) => handleEmojiClick(emojiData)} />
                  </div>
                )}
              </div>

              {/* Mention - only show if mentionProvider is provided */}
              {mentionProvider && (
                <button
                  type="button"
                  onClick={() => editor.chain().focus().insertContent('@').run()}
                  className="p-2 rounded hover:bg-app-grey-20 transition-colors"
                  title="Mention User"
                >
                  <MentionIcon />
                </button>
              )}

              {/* Link */}
              <button
                type="button"
                onClick={handleLinkClick}
                className={`p-2 rounded hover:bg-app-grey-20 transition-colors ${
                  editor.isActive('link') ? 'bg-app-grey-20' : ''
                }`}
                title={editor.isActive('link') ? 'Edit Link' : 'Insert Link (Ctrl+K)'}
              >
                <HyperlinkIcon />
              </button>

              {/* Divider */}
              <div className="w-px h-6 bg-app-grey-25 mx-1" />

              {/* Bold */}
              <button
                type="button"
                onClick={() => editor.chain().focus().toggleBold().run()}
                className={`p-2 rounded hover:bg-app-grey-20 transition-colors ${
                  editor.isActive('bold') ? 'bg-app-grey-20' : ''
                }`}
                title="Bold (Ctrl+B)"
              >
                <BoldIcon />
              </button>

              {/* Italic */}
              <button
                type="button"
                onClick={() => editor.chain().focus().toggleItalic().run()}
                className={`p-2 rounded hover:bg-app-grey-20 transition-colors ${
                  editor.isActive('italic') ? 'bg-app-grey-20' : ''
                }`}
                title="Italic (Ctrl+I)"
              >
                <ItalicIcon />
              </button>

              {/* Strikethrough */}
              <button
                type="button"
                onClick={() => editor.chain().focus().toggleStrike().run()}
                className={`p-2 rounded hover:bg-app-grey-20 transition-colors ${
                  editor.isActive('strike') ? 'bg-app-grey-20' : ''
                }`}
                title="Strikethrough"
              >
                <StrikethroughIcon />
              </button>
              <div className="w-px h-6 bg-app-grey-25 mx-1" />
              {/* Bullet List */}
              <button
                type="button"
                onClick={() => {
                  if (editor.isActive('bulletList')) {
                    // If already in a bullet list, lift it out to a paragraph
                    editor.chain().focus().liftListItem('listItem').run()
                    return
                  }

                  const { state } = editor
                  const { $from } = state.selection

                  // Check if we're right after a hard break
                  const nodeBefore = $from.nodeBefore
                  if (nodeBefore && nodeBefore.type.name === 'hardBreak') {
                    // Replace the hard break with a paragraph split, then toggle bullet
                    const hardBreakPos = $from.pos - nodeBefore.nodeSize
                    editor
                      .chain()
                      .focus()
                      .setTextSelection(hardBreakPos)
                      .deleteNode('hardBreak')
                      .splitBlock()
                      .toggleBulletList()
                      .run()
                    return
                  }

                  // If cursor is in middle of text (has selection or text before cursor), split first
                  const posInParent = $from.parentOffset
                  const parentNode = $from.parent

                  if (
                    !editor.isActive('bulletList') &&
                    posInParent > 0 &&
                    posInParent < parentNode.content.size
                  ) {
                    // Split at cursor position, then apply bullet to second part
                    editor.chain().focus().splitBlock().toggleBulletList().run()
                    return
                  }

                  // Default: toggle bullet list
                  editor.chain().focus().toggleBulletList().run()
                }}
                className={`p-2 rounded hover:bg-app-grey-20 transition-colors ${
                  editor.isActive('bulletList') ? 'bg-app-grey-20' : ''
                }`}
                title="Bullet List"
              >
                <ListBulletIcon />
              </button>
            </div>

            <div className="flex items-center gap-2">
              {rightActions}
              {/* Only show submit button in message mode */}
              {mode === 'message' && (
                <button
                  type="submit"
                  disabled={
                    (editor.isEmpty && pendingUploads.length === 0 && !hasVoiceMessage) || disabled
                  }
                  className="w-9 h-9 rounded-2xl bg-app-primary-60 text-white flex items-center justify-center hover:bg-app-primary-70 disabled:bg-app-primary-20 disabled:text-app-primary-60 disabled:cursor-not-allowed transition-colors shadow-sm"
                  title="Send"
                >
                  <AppSendMessageIcon />
                </button>
              )}
            </div>
          </div>
        </div>
      </form>

      {/* Link Dialog */}
      <LinkDialog
        isOpen={showLinkDialog}
        onClose={() => setShowLinkDialog(false)}
        onSave={handleLinkSave}
        onRemove={handleLinkRemove}
        initialText={linkDialogData.text}
        initialUrl={linkDialogData.url}
        isEditing={linkDialogData.isEditing}
      />

      {/* Styles */}
      <style>{`
        .rich-text-input-container .ProseMirror {
          outline: none;
          min-height: 56px;
          padding: 8px 12px;
          border: none;
        }
        .rich-text-input-container .ProseMirror p.is-editor-empty:first-child::before {
          color: var(--color-app-grey-60, #7C7C7C);
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
          position: relative;
          top: 0;
        }
      `}</style>
    </div>
  )
}
